import { randomUUID } from 'node:crypto';
import type { RunItem } from '../items';
import type { AgentInputItem } from '../types';

export type EnsureItemIdMode = 'preserve' | 'generated';

export function ensureAgentInputItemIds(
  items: AgentInputItem[],
  options: {
    usedIds?: Set<string>;
    mode?: EnsureItemIdMode;
  } = {},
): AgentInputItem[] {
  const usedIds = options.usedIds ?? new Set<string>();
  const mode = options.mode ?? 'preserve';
  return items.map((item) => ensureAgentInputItemId(item, usedIds, mode));
}

export function ensureRunItemIds(
  items: RunItem[],
  options: {
    usedIds?: Set<string>;
    mode?: EnsureItemIdMode;
  } = {},
): void {
  const usedIds = options.usedIds ?? new Set<string>();
  const mode = options.mode ?? 'preserve';
  for (const item of items) {
    if (!item.rawItem || !isAgentInputItemLike(item.rawItem)) {
      continue;
    }
    item.rawItem = ensureAgentInputItemId(
      item.rawItem as AgentInputItem,
      usedIds,
      mode,
    ) as never;
  }
}

export function collectAgentInputItemIds(
  items: Array<AgentInputItem | RunItem | undefined>,
): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    const rawItem = getRawAgentInputItem(item);
    if (!rawItem) {
      continue;
    }
    const id = getItemId(rawItem);
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

function ensureAgentInputItemId<T extends AgentInputItem>(
  item: T,
  usedIds: Set<string>,
  mode: EnsureItemIdMode,
): T {
  if (!item || typeof item !== 'object') {
    return item;
  }

  const record = item as Record<string, unknown>;
  const existingId = getItemId(record);
  if (mode === 'preserve' && existingId && !usedIds.has(existingId)) {
    usedIds.add(existingId);
    return item;
  }

  if (
    mode === 'generated' &&
    existingId &&
    hasProviderItemId(record) &&
    !usedIds.has(existingId)
  ) {
    usedIds.add(existingId);
    return item;
  }

  const cloned = structuredClone(item);
  const clonedRecord = cloned as Record<string, unknown>;
  if (mode === 'generated' && existingId) {
    preserveProviderItemId(clonedRecord, existingId);
  }

  const nextId = randomUniqueUuid(usedIds);
  clonedRecord.id = nextId;
  usedIds.add(nextId);
  return cloned;
}

function preserveProviderItemId(record: Record<string, unknown>, id: string) {
  const providerData =
    record.providerData && typeof record.providerData === 'object'
      ? { ...(record.providerData as Record<string, unknown>) }
      : {};
  if (typeof providerData.providerItemId !== 'string') {
    providerData.providerItemId = id;
  }
  record.providerData = providerData;
}

function hasProviderItemId(record: Record<string, unknown>): boolean {
  const providerData = record.providerData;
  return (
    Boolean(providerData) &&
    typeof providerData === 'object' &&
    typeof (providerData as Record<string, unknown>).providerItemId === 'string'
  );
}

function randomUniqueUuid(usedIds: Set<string>): string {
  let id = randomUUID();
  while (usedIds.has(id)) {
    id = randomUUID();
  }
  return id;
}

function getRawAgentInputItem(
  item: AgentInputItem | RunItem | undefined,
): AgentInputItem | undefined {
  if (!item) {
    return undefined;
  }
  if ('rawItem' in item) {
    return isAgentInputItemLike(item.rawItem)
      ? (item.rawItem as AgentInputItem)
      : undefined;
  }
  return isAgentInputItemLike(item) ? item : undefined;
}

function getItemId(item: Record<string, unknown>): string | undefined {
  const id = item.id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function isAgentInputItemLike(value: unknown): value is AgentInputItem {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as { type?: unknown; role?: unknown };
  if (typeof record.role === 'string') {
    return true;
  }
  if (typeof record.type !== 'string') {
    return false;
  }
  return (
    record.type !== 'compact_boundary' && record.type !== 'compact_summary'
  );
}
